import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  ShieldCheck,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import inovaone_api from "@/services/inovaone_api";

// Mesmo token público usado pelo loginAutomatico em UsuarioContext —
// a API o aceita como bypass do JWT pra rotas sem auth do usuário final.
const SOCKET_TOKEN =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InNvY2tldCIsImlhdCI6MTczODk0NzA0NSwiZXhwIjoxNzM4OTc1ODQ1fQ._ZvpzF1JTVKCRIWctHTDTF_8WZU4vYz2mFE8Cg_ghM8";
const SOCKET_HEADERS = {
  Authorization: `Bearer ${SOCKET_TOKEN}`,
  id_conta: "socket",
};

type FormErrors = Partial<{
  nomeEmpresa: string;
  emailEmpresa: string;
  nomeAdmin: string;
  emailAdmin: string;
  senha: string;
  confirmarSenha: string;
  termos: string;
}>;

function isEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export function CadastroForm({
  className,
  ...props
}: React.ComponentProps<"form">) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // dados empresa
  const [nomeEmpresa, setNomeEmpresa] = useState("");
  const [emailEmpresa, setEmailEmpresa] = useState("");
  const [telefone, setTelefone] = useState("");
  const [cnpj, setCnpj] = useState("");

  // dados admin
  const [nomeAdmin, setNomeAdmin] = useState("");
  const [emailAdmin, setEmailAdmin] = useState("");
  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [termos, setTermos] = useState(false);

  // estado geral
  const [carregando, setCarregando] = useState(false);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [sucesso, setSucesso] = useState(false);

  const detectCapsLock = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (typeof e.getModifierState === "function") {
      setCapsLock(e.getModifierState("CapsLock"));
    }
  };

  const validar = (): boolean => {
    const next: FormErrors = {};
    if (!nomeEmpresa.trim()) next.nomeEmpresa = t("cadastro.errCompanyName");
    if (!emailEmpresa.trim())
      next.emailEmpresa = t("cadastro.errCompanyEmail");
    else if (!isEmail(emailEmpresa))
      next.emailEmpresa = t("cadastro.errEmailInvalid");

    if (!nomeAdmin.trim()) next.nomeAdmin = t("cadastro.errAdminName");
    if (!emailAdmin.trim()) next.emailAdmin = t("cadastro.errAdminEmail");
    else if (!isEmail(emailAdmin)) next.emailAdmin = t("cadastro.errEmailInvalid");

    if (!senha) next.senha = t("cadastro.errPasswordRequired");
    else if (senha.length < 8)
      next.senha = t("cadastro.errPasswordMin");

    if (!confirmarSenha) next.confirmarSenha = t("cadastro.errConfirmRequired");
    else if (confirmarSenha !== senha)
      next.confirmarSenha = t("cadastro.errPasswordMismatch");

    if (!termos) next.termos = t("cadastro.errTerms");

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErroGeral(null);
    if (!validar()) return;

    setCarregando(true);
    try {
      // 1. Criar empresa
      let id_conta: string;
      try {
        const contaRes = await inovaone_api.post(
          "/conta",
          [
            {
              id_conta: "0",
              nome: nomeEmpresa.trim(),
              email: emailEmpresa.trim().toLowerCase(),
              telefone: telefone.trim() || undefined,
              cnpj: cnpj.trim() || undefined,
            },
          ],
          { headers: SOCKET_HEADERS },
        );
        const novaConta = contaRes.data?.[0];
        if (!novaConta?._id || novaConta?.error) {
          throw new Error(
            (typeof novaConta?.error === "string"
              ? novaConta.error
              : null) || t("cadastro.errCompanyCreate"),
          );
        }
        id_conta = novaConta._id;
      } catch (e: any) {
        const msg =
          e?.response?.data?.error ||
          e?.message ||
          t("cadastro.errCompanyCreate");
        if (typeof msg === "string" && msg.toLowerCase().includes("email")) {
          setErrors((prev) => ({
            ...prev,
            emailEmpresa: t("cadastro.errCompanyEmailInUse"),
          }));
        } else {
          setErroGeral(String(msg));
        }
        return;
      }

      // 2. Criar usuário admin
      try {
        await inovaone_api.post(
          "/usuario",
          [
            {
              id_usuario: "0",
              id_conta,
              nome: nomeAdmin.trim(),
              email: emailAdmin.trim().toLowerCase(),
              login: emailAdmin.trim().toLowerCase(),
              senha,
              principal: 1,
              ativo: 1,
              perfil: "Administrador",
            },
          ],
          { headers: SOCKET_HEADERS },
        );
      } catch (e: any) {
        const msg =
          e?.response?.data?.error ||
          e?.message ||
          t("cadastro.errAdminCreate");
        if (
          typeof msg === "string" &&
          (msg.toLowerCase().includes("login") ||
            msg.toLowerCase().includes("email"))
        ) {
          setErrors((prev) => ({
            ...prev,
            emailAdmin: t("cadastro.errAdminEmailInUse"),
          }));
        } else {
          setErroGeral(
            t("cadastro.errCompanyCreatedUserFailed", { msg }),
          );
        }
        return;
      }

      setSucesso(true);
    } finally {
      setCarregando(false);
    }
  };

  // ---- Tela de sucesso ----
  if (sucesso) {
    const emailExibir = emailAdmin.trim().toLowerCase();
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 ring-4 ring-emerald-500/10">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <div className="space-y-1.5">
            <h1 className="text-2xl font-bold tracking-tight">
              {t("cadastro.successTitle")}
            </h1>
            <p className="text-sm text-muted-foreground text-balance">
              {t("cadastro.successDesc")}
            </p>
          </div>
        </div>

        <div className="rounded-md border bg-muted/30 p-4 space-y-3">
          <div className="flex items-start gap-2.5">
            <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div className="text-xs space-y-1">
              <p className="font-medium text-foreground">
                {t("cadastro.nextSteps")}
              </p>
              <ol className="list-decimal pl-4 space-y-0.5 text-muted-foreground">
                <li>{t("cadastro.nextStep1")}</li>
                <li>
                  {t("cadastro.nextStep2Prefix")}{" "}
                  <span className="text-foreground">
                    {emailExibir}
                  </span>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <Button onClick={() => navigate("/auth", { replace: true })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t("cadastro.backToLogin")}
        </Button>
      </div>
    );
  }

  // ---- Formulário ----
  return (
    <form
      className={cn("flex flex-col gap-6", className)}
      onSubmit={handleSubmit}
      {...props}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold tracking-tight">
          {t("cadastro.title")}
        </h1>
        <p className="text-muted-foreground text-sm text-balance">
          {t("cadastro.subtitle")}
        </p>
      </div>

      {/* Seção: empresa */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t("cadastro.sectionCompany")}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="grid gap-3">
          <div className="grid gap-2">
            <Label htmlFor="nome-empresa">
              {t("cadastro.companyName")}{" "}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="nome-empresa"
              autoFocus
              value={nomeEmpresa}
              onChange={(e) => {
                setNomeEmpresa(e.target.value);
                if (errors.nomeEmpresa)
                  setErrors((p) => ({ ...p, nomeEmpresa: undefined }));
              }}
              aria-invalid={!!errors.nomeEmpresa}
              placeholder={t("cadastro.companyNamePlaceholder")}
            />
            {errors.nomeEmpresa && (
              <p className="text-xs text-destructive">{errors.nomeEmpresa}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email-empresa">
              {t("cadastro.corporateEmail")}{" "}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email-empresa"
              type="email"
              autoComplete="email"
              value={emailEmpresa}
              onChange={(e) => {
                setEmailEmpresa(e.target.value);
                if (errors.emailEmpresa)
                  setErrors((p) => ({ ...p, emailEmpresa: undefined }));
              }}
              aria-invalid={!!errors.emailEmpresa}
              placeholder={t("cadastro.corporateEmailPlaceholder")}
            />
            {errors.emailEmpresa && (
              <p className="text-xs text-destructive">{errors.emailEmpresa}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="telefone">
                {t("cadastro.phone")}
                <span className="text-muted-foreground/60 text-xs ml-1">
                  {t("cadastro.optional")}
                </span>
              </Label>
              <Input
                id="telefone"
                type="tel"
                autoComplete="tel"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                placeholder="(11) 99999-9999"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cnpj">
                {t("cadastro.cnpj")}
                <span className="text-muted-foreground/60 text-xs ml-1">
                  {t("cadastro.optional")}
                </span>
              </Label>
              <Input
                id="cnpj"
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                placeholder="00.000.000/0000-00"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Seção: admin */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t("cadastro.sectionAccess")}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="grid gap-3">
          <div className="grid gap-2">
            <Label htmlFor="nome-admin">
              {t("cadastro.fullName")} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="nome-admin"
              autoComplete="name"
              value={nomeAdmin}
              onChange={(e) => {
                setNomeAdmin(e.target.value);
                if (errors.nomeAdmin)
                  setErrors((p) => ({ ...p, nomeAdmin: undefined }));
              }}
              aria-invalid={!!errors.nomeAdmin}
              placeholder={t("cadastro.fullNamePlaceholder")}
            />
            {errors.nomeAdmin && (
              <p className="text-xs text-destructive">{errors.nomeAdmin}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email-admin">
              {t("cadastro.accessEmail")}{" "}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email-admin"
              type="email"
              autoComplete="username"
              value={emailAdmin}
              onChange={(e) => {
                setEmailAdmin(e.target.value);
                if (errors.emailAdmin)
                  setErrors((p) => ({ ...p, emailAdmin: undefined }));
              }}
              aria-invalid={!!errors.emailAdmin}
              placeholder={t("cadastro.accessEmailPlaceholder")}
            />
            {errors.emailAdmin && (
              <p className="text-xs text-destructive">{errors.emailAdmin}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="senha">
                {t("cadastro.password")}{" "}
                <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="senha"
                  type={mostrarSenha ? "text" : "password"}
                  autoComplete="new-password"
                  value={senha}
                  onChange={(e) => {
                    setSenha(e.target.value);
                    if (errors.senha)
                      setErrors((p) => ({ ...p, senha: undefined }));
                  }}
                  onKeyDown={detectCapsLock}
                  onKeyUp={detectCapsLock}
                  onBlur={() => setCapsLock(false)}
                  aria-invalid={!!errors.senha}
                  className="pr-10"
                  placeholder={t("cadastro.passwordPlaceholder")}
                />
                <button
                  type="button"
                  className="absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  tabIndex={-1}
                  aria-label={
                    mostrarSenha
                      ? t("login.hidePassword")
                      : t("login.showPassword")
                  }
                  aria-pressed={mostrarSenha}
                >
                  {mostrarSenha ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.senha && (
                <p className="text-xs text-destructive">{errors.senha}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="confirmar-senha">
                {t("cadastro.confirmPassword")}{" "}
                <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="confirmar-senha"
                  type={mostrarConfirmar ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmarSenha}
                  onChange={(e) => {
                    setConfirmarSenha(e.target.value);
                    if (errors.confirmarSenha)
                      setErrors((p) => ({
                        ...p,
                        confirmarSenha: undefined,
                      }));
                  }}
                  onKeyDown={detectCapsLock}
                  onKeyUp={detectCapsLock}
                  onBlur={() => setCapsLock(false)}
                  aria-invalid={!!errors.confirmarSenha}
                  className="pr-10"
                  placeholder={t("cadastro.confirmPasswordPlaceholder")}
                />
                <button
                  type="button"
                  className="absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                  tabIndex={-1}
                  aria-label={
                    mostrarConfirmar
                      ? t("login.hidePassword")
                      : t("login.showPassword")
                  }
                  aria-pressed={mostrarConfirmar}
                >
                  {mostrarConfirmar ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.confirmarSenha && (
                <p className="text-xs text-destructive">
                  {errors.confirmarSenha}
                </p>
              )}
            </div>
          </div>

          {capsLock && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
              <KeyRound className="h-3 w-3" />
              {t("cadastro.capsLock")}
            </p>
          )}
        </div>
      </div>

      {/* Termos */}
      <div className="space-y-1.5">
        <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
          <Checkbox
            checked={termos}
            onCheckedChange={(v) => {
              setTermos(Boolean(v));
              if (errors.termos)
                setErrors((p) => ({ ...p, termos: undefined }));
            }}
            className="mt-0.5"
            aria-invalid={!!errors.termos}
          />
          <span className="text-muted-foreground leading-tight">
            {t("cadastro.acceptTermsPrefix")}{" "}
            <a
              href="https://www.inovacode.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 text-foreground"
            >
              {t("cadastro.termsOfUse")}
            </a>{" "}
            {t("cadastro.and")}{" "}
            <a
              href="https://www.inovacode.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 text-foreground"
            >
              {t("cadastro.privacyPolicy")}
            </a>
            .
          </span>
        </label>
        {errors.termos && (
          <p className="text-xs text-destructive pl-6">{errors.termos}</p>
        )}
      </div>

      {/* Erro geral */}
      {erroGeral && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive dark:text-red-400"
        >
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{erroGeral}</span>
        </div>
      )}

      <Button type="submit" className="w-full" disabled={carregando}>
        {carregando ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("cadastro.creating")}
          </>
        ) : (
          t("cadastro.submit")
        )}
      </Button>

      <div className="text-center text-sm select-none">
        {t("cadastro.haveAccount")}{" "}
        <Link
          to="/auth"
          className="underline underline-offset-4 font-medium"
        >
          {t("cadastro.signIn")}
        </Link>
      </div>
    </form>
  );
}
